import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Dialog } from '@material-ui/core';
import * as Sentry from '@sentry/react';

import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import { TextField } from 'core/components/text-field';
import alertIcon from 'core/assets/svg/triangle-alert.svg';
import useStyles from './application-error.styles';

// Staff-wide "application error" experience shown by the React error boundary
// (GitHub #30224). Replaces the old red "Application Error" dialog with a calmer,
// on-brand modal. The error is already captured by Sentry.ErrorBoundary; if the
// operator types additional details, we attach them to that Sentry event as
// feedback before navigating away (this is the dead "report feedback" textbox
// from the old design, now actually wired up).
const ApplicationError = ({ eventId }) => {
  const classes = useStyles();
  const [details, setDetails] = useState('');

  const submitFeedbackAnd = (navigate) => () => {
    const trimmed = details?.trim();
    if (trimmed && eventId) {
      Sentry.captureFeedback({ associatedEventId: eventId, message: trimmed });
    }
    navigate();
  };

  return (
    <Dialog open classes={{ paper: classes.dialog }}>
      <div className={classes.body}>
        <div className={classes.header}>
          <img src={alertIcon} alt="" className={classes.alertIcon} />
          <Label type="title" className={classes.title}>
            We couldn&rsquo;t complete your request
          </Label>
          <Label type="body1" className={classes.intro}>
            We hit a problem on our end, and the request didn&rsquo;t go through.
          </Label>
          <Label type="body1" className={classes.intro}>
            We&rsquo;ve already shared the details with our support team. Any unsaved changes will need to be entered
            again.
          </Label>
        </div>
        <div className={classes.field}>
          <div className={classes.labelRow}>
            <Label type="infoLabel" className={classes.fieldLabel}>
              Additional details
            </Label>
            <Label type="infoLabel" className={classes.optional}>
              Optional
            </Label>
          </div>
          <TextField
            mode="light"
            fullWidth
            multiline
            minRows={3}
            maxRows={6}
            value={details}
            onChange={(event) => setDetails(event.target.value)}
            placeholder="Add any details that might help our support team"
          />
        </div>
        <div className={classes.buttons}>
          <Button
            onClick={submitFeedbackAnd(() => {
              window.location.href = '/';
            })}
            variant="contained"
            color="secondary"
            className={classes.button}
          >
            Go to home
          </Button>
          <Button
            onClick={submitFeedbackAnd(() => {
              window.location.reload();
            })}
            variant="contained"
            color="primary"
            className={classes.button}
          >
            Refresh page
          </Button>
        </div>
      </div>
    </Dialog>
  );
};

ApplicationError.propTypes = {
  eventId: PropTypes.string
};

export default ApplicationError;
